import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '../utils/toast';
import { resendVerification } from '../services/users';
import LanguageSwitcher from '../components/languageSwitcher';
import './auth.css';

const RESEND_COOLDOWN_S = 60

const ThankYouPage = () => {

    const { t } = useTranslation();
    const location = useLocation()
    const email = location.state && location.state.email

    const [isResending, setIsResending] = useState(false)
    const [cooldown, setCooldown] = useState(0)

    useEffect(() => {
        if (cooldown <= 0) return
        const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000)
        return () => clearInterval(id)
    }, [cooldown])

    const handleResend = async () => {
        if (!email || isResending || cooldown > 0) return
        setIsResending(true)
        try {
            await resendVerification(email)
            toast.success(t('auth.verificationSentToast'), {
                position: toast.POSITION.BOTTOM_RIGHT
            })
            setCooldown(RESEND_COOLDOWN_S)
        } catch (e) {
            toast.error(t('auth.verificationSendError'), {
                position: toast.POSITION.BOTTOM_RIGHT
            })
        } finally {
            setIsResending(false)
        }
    }

    const canResend = !!email && !isResending && cooldown === 0

    return (
        <div className="auth-page">
            <div className="auth-shell">

                {/* ───────── LEFT / BRAND PANEL ───────── */}
                <aside className="auth-brand-panel">
                    <div className="auth-brand-top">
                        <Link to="/login" className="auth-brand-link" aria-label={t('auth.logoGoToSignIn')}>
                            <img
                                src={process.env.PUBLIC_URL + '/logo-soroby.svg'}
                                alt="Soroby"
                                className="auth-brand-logo"
                            />
                        </Link>
                    </div>

                    <div className="auth-brand-message">
                        <h1 className="auth-brand-title" dangerouslySetInnerHTML={{ __html: t('auth.brandTitle') }} />
                        <p className="auth-brand-tagline">
                            {t('auth.brandTagline')}
                        </p>
                    </div>

                    <div className="auth-brand-footer">
                        <span>© {new Date().getFullYear()} Soroby · {t('auth.builtForServiceBusinesses')}</span>
                        <span>soroby.com</span>
                    </div>
                </aside>

                {/* ───────── RIGHT / SUCCESS PANEL ───────── */}
                <main className="auth-form-panel">
                    <LanguageSwitcher className="auth-lang" id="auth-language-drop" drop="down" align="end" showIcon />

                    <div className="auth-form-inner">

                        <div className="auth-mobile-brand">
                            <Link to="/login" className="auth-brand-link" aria-label={t('auth.logoGoToSignIn')}>
                                <img src={process.env.PUBLIC_URL + '/logo-soroby.svg'} alt="Soroby" />
                            </Link>
                        </div>

                        <div className="auth-success-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="5" width="18" height="14" rx="2" />
                                <path d="M3 7l9 6 9-6" />
                            </svg>
                        </div>

                        <div className="auth-form-heading">
                            <h2>{t('auth.thankYouTitle')}</h2>
                            <p>
                                {t('auth.thankYouCheckInbox')}
                            </p>
                        </div>

                        {email && (
                            <div className="auth-email-chip">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <rect x="3" y="5" width="18" height="14" rx="2" />
                                    <path d="M3 7l9 6 9-6" />
                                </svg>
                                <span>{email}</span>
                            </div>
                        )}

                        <Link to="/login" className="auth-btn">
                            <span>{t('auth.goToSignIn')}</span>
                            <span className="arrow" aria-hidden="true">→</span>
                        </Link>

                        <p className="auth-secondary">
                            {t('auth.didntGetEmail')} {t('auth.checkSpamOr')}{' '}
                            {email ? (
                                <button
                                    type="button"
                                    className="auth-inline-btn"
                                    onClick={handleResend}
                                    disabled={!canResend}
                                >
                                    {isResending
                                        ? t('auth.sending').toLowerCase()
                                        : cooldown > 0
                                            ? t('auth.resendInSeconds', { seconds: cooldown })
                                            : t('auth.resendIt')}
                                </button>
                            ) : (
                                <Link to="/register">{t('auth.tryAgain')}</Link>
                            )}
                            .
                        </p>
                    </div>
                </main>

            </div>
        </div>
    )
}

export default ThankYouPage;
